/**
 * Live-mode weather, the live half of loader-weather.js with the detection
 * rule from loader-app.js 1442-1490: "present" means the sim clock is within
 * 24 h of real time. While present (and the live_nws_weather toggle is on) the
 * NWS gridpoint forecast (api.weather.gov/points -> forecastGridData) gives an
 * impact per US antenna through impactFromForecast; non-US antennas and failed
 * requests take the static weatherImpactFactor. Refreshed every 10 min with a
 * 30 min impact cache that survives leaving and re-entering present time.
 */
import { impactFromForecast, isUsLocation, staticImpact, type NwsGridData } from '../analysis';
import type { WeatherImpact } from '../analysis/types';
import { WEATHER } from '../constants';
import type { Antenna } from '../types';
import { useAwareness } from './store';
import { errorMessage } from './util';

export const LIVE_MODE_WINDOW_MS = 86_400_000;
export const WEATHER_REFRESH_MS = WEATHER.REFRESH_INTERVAL_MS;
export const IMPACT_CACHE_MS = WEATHER.IMPACT_CACHE_MS;
export const LIVE_WEATHER_TOGGLE = 'live_nws_weather';

/** Within 24 h of real time, either side (strictly less, as the original compared seconds < 86400). */
export function isNearPresent(simMs: number, realMs: number): boolean {
  return Math.abs(simMs - realMs) < LIVE_MODE_WINDOW_MS;
}

export function nwsPointsUrl(lat: number, lon: number): string {
  return `https://api.weather.gov/points/${lat.toFixed(4)},${lon.toFixed(4)}`;
}

export type JsonFetcher = (url: string) => Promise<unknown>;

async function defaultFetchJson(url: string): Promise<unknown> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url} returned HTTP ${res.status}`);
  return res.json();
}

/** Impact for one antenna: points lookup, then the gridpoint forecast; any failure gives the static impact. */
export async function fetchAntennaImpact(antenna: Antenna, fetchJson: JsonFetcher = defaultFetchJson): Promise<WeatherImpact> {
  if (!isUsLocation(antenna)) return staticImpact(antenna, 'N/A (non-US)');
  try {
    const point = (await fetchJson(nwsPointsUrl(antenna.location.lat, antenna.location.lon))) as {
      properties?: { forecastGridData?: string };
    };
    const forecastUrl = point?.properties?.forecastGridData;
    if (!forecastUrl) return staticImpact(antenna);
    const grid = (await fetchJson(forecastUrl)) as NwsGridData;
    return impactFromForecast(antenna, grid);
  } catch (err) {
    console.warn(`[liveWeather] NWS forecast for ${antenna.id} unavailable; using the static impact:`, errorMessage(err));
    return staticImpact(antenna);
  }
}

/** Impacts for every antenna, fetched in parallel as the original did. */
export async function computeWeatherImpacts(antennas: readonly Antenna[], fetchJson: JsonFetcher = defaultFetchJson): Promise<Record<string, WeatherImpact>> {
  const entries = await Promise.all(antennas.map(async (a) => [a.id, await fetchAntennaImpact(a, fetchJson)] as const));
  const impacts: Record<string, WeatherImpact> = {};
  for (const [id, impact] of entries) impacts[id] = impact;
  return impacts;
}

interface ImpactCache {
  impacts: Record<string, WeatherImpact>;
  atMs: number;
}

/** Module-level like the original's _weatherImpactCache: kept across live-mode exits. */
let impactCache: ImpactCache | null = null;

export function clearImpactCache(): void {
  impactCache = null;
}

export function cachedImpacts(nowMs: number): ImpactCache | null {
  if (impactCache && nowMs - impactCache.atMs < IMPACT_CACHE_MS) return impactCache;
  return null;
}

/**
 * Refresh the impacts unless the cache is younger than 30 min; publishes to the
 * store either way. Concurrent refreshes are coalesced.
 */
let inflight: Promise<void> | null = null;

export function refreshWeatherImpacts(
  antennas: readonly Antenna[],
  options: { fetchJson?: JsonFetcher; nowMs?: () => number; force?: boolean; shouldPublish?: () => boolean } = {},
): Promise<void> {
  const now = options.nowMs ?? Date.now;
  const publish = (impacts: Record<string, WeatherImpact>, atMs: number) => {
    if (options.shouldPublish && !options.shouldPublish()) return;
    useAwareness.getState().setWeatherImpacts(impacts, atMs);
  };
  const cached = options.force ? null : cachedImpacts(now());
  if (cached) {
    publish(cached.impacts, cached.atMs);
    return Promise.resolve();
  }
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const impacts = await computeWeatherImpacts(antennas, options.fetchJson);
      impactCache = { impacts, atMs: now() };
      publish(impacts, impactCache.atMs);
    } catch (err) {
      console.error('[liveWeather] impact refresh failed:', err);
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Live mode: compute now (cache permitting) and every 10 min until stopped;
 * the published impacts are cleared on stop (leaving present time) and a
 * refresh that finishes after the stop is cached but not published.
 */
export function startLiveWeather(getAntennas: () => readonly Antenna[], options: { fetchJson?: JsonFetcher } = {}): () => void {
  let active = true;
  const run = () => {
    void refreshWeatherImpacts(getAntennas(), { ...options, shouldPublish: () => active });
  };
  run();
  const id = setInterval(run, WEATHER_REFRESH_MS);
  return () => {
    active = false;
    clearInterval(id);
    useAwareness.getState().setWeatherImpacts(null, null);
  };
}
